import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'react-toastify'
import Button from 'src/components/Button'
import { ReactComponent as CloseIcon } from 'src/assets/svg/close.svg'
import { ReactComponent as InfoIcon } from 'src/assets/svg/ri-information-fill.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { ReactComponent as ExportIcon } from 'src/assets/svg/blue-file-download.svg'
import { useGetCampaignsQuery } from 'src/reactQuery/useGetCampaignsQuery'
import { useExportCampaignComparisonExcelQuery } from 'src/reactQuery/useExportCampaignComparisonExcelQuery'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import styles from './campaignComparisonModal.module.scss'

interface SelectedCampaign {
  id: string
  name: string
}

interface CampaignComparisonModalProps {
  benchmarkCampaign?: string | null
  isOpen: boolean
  onClose: () => void
  onCompare?: (campaigns: SelectedCampaign[], benchmark: string | null) => void
  selectedCampaigns?: SelectedCampaign[]
}

const MAX_CAMPAIGNS = 9

export const CampaignComparisonModal: FC<CampaignComparisonModalProps> = ({
  benchmarkCampaign: initialBenchmark,
  isOpen,
  onClose,
  onCompare,
  selectedCampaigns: initialCampaigns,
}) => {
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([])
  const [benchmarkedCampaign, setBenchmarkedCampaign] = useState<string | null>(
    null
  )
  const [searchTerm, setSearchTerm] = useState('')

  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const dates = getDateRangeByQueryParams(queryParams)

  const { data: campaignsData, isLoading } = useGetCampaignsQuery({
    end: dates?.end,
    start: dates?.start,
  })

  const { mutate: exportComparison, isPending: isExporting } =
    useExportCampaignComparisonExcelQuery()

  // Initialize state when modal opens with existing selections
  useEffect(() => {
    if (isOpen && initialCampaigns) {
      // If more than MAX_CAMPAIGNS are selected, clear the selection
      // (This happens when "all campaigns" is loaded initially)
      if (initialCampaigns.length > MAX_CAMPAIGNS) {
        setSelectedCampaigns([])
        setBenchmarkedCampaign(null)
      } else {
        setSelectedCampaigns(initialCampaigns.map((c) => c.id))
        setBenchmarkedCampaign(initialBenchmark || null)
      }
    }
  }, [isOpen, initialCampaigns, initialBenchmark])

  const campaigns = campaignsData?.campaigns || []
  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCampaignToggle = (campaignId: string) => {
    if (selectedCampaigns.includes(campaignId)) {
      setSelectedCampaigns((prev) => prev.filter((id) => id !== campaignId))
      if (benchmarkedCampaign === campaignId) {
        setBenchmarkedCampaign(null)
      }
    } else if (selectedCampaigns.length < MAX_CAMPAIGNS) {
      setSelectedCampaigns((prev) => [...prev, campaignId])
    }
  }

  const handleBenchmarkToggle = (campaignId: string) => {
    if (benchmarkedCampaign === campaignId) {
      setBenchmarkedCampaign(null)
    } else {
      setBenchmarkedCampaign(campaignId)
      if (!selectedCampaigns.includes(campaignId)) {
        handleCampaignToggle(campaignId)
      }
    }
  }

  const handleCompare = () => {
    // Allow comparing with 0 campaigns (means ALL) or 2+ campaigns
    if (selectedCampaigns.length === 0 || selectedCampaigns.length >= 2) {
      if (onCompare) {
        const selectedCampaignsData = selectedCampaigns.map((id) => {
          const campaign = campaigns.find((c) => c._id === id)
          return {
            id,
            name: campaign?.name || id,
          }
        })
        onCompare(selectedCampaignsData, benchmarkedCampaign)
      }
      onClose()
    }
  }

  const handleExportExcel = () => {
    // Allow 0 (all campaigns), or 2-9 campaigns
    if (selectedCampaigns.length === 1 || selectedCampaigns.length > 9) {
      toast.error('Please select 2-9 campaigns or none for all campaigns')
      return
    }

    // Format dates to ISO format if available
    const fromDate = dates?.start
      ? new Date(dates.start).toISOString()
      : undefined
    const toDate = dates?.end ? new Date(dates.end).toISOString() : undefined

    exportComparison(
      {
        campaignIds: selectedCampaigns,
        fromDate,
        metrics: ['mentions', 'sources', 'sentiments'],
        toDate,
      },
      {
        onError: (error) => {
          console.error('Export failed:', error)
          toast.error('Failed to export Excel. Please try again.')
        },
        onSuccess: (blob) => {
          // Create download link
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `campaign-comparison-${
            new Date().toISOString().split('T')[0]
          }.xlsx`
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          toast.success('Excel exported successfully!')
        },
      }
    )
  }

  const handleClose = () => {
    setSelectedCampaigns([])
    setBenchmarkedCampaign(null)
    setSearchTerm('')
    onClose()
  }

  if (!isOpen) return null

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={handleClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Compare Campaigns</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.infoBox}>
          <InfoIcon className={styles.infoIcon} />
          <p className={styles.infoText}>
            You can compare up to 9 campaigns at a time. To view the full report
            with charts, use the Export Excel button below.
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.campaignSelection}>
            <label className={styles.sectionLabel}>Choose Campaigns:</label>

            <div className={styles.campaignBox}>
              <div className={styles.searchRow}>
                <input
                  className={styles.searchInput}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Campaign"
                  type="text"
                  value={searchTerm}
                />
                <div className={styles.counter}>
                  <span className={styles.counterNumber}>
                    {selectedCampaigns.length}
                  </span>{' '}
                  Campaigns Selected
                </div>
              </div>

              <div className={styles.selectActions}>
                <button
                  className={styles.selectButton}
                  disabled={filteredCampaigns.length === 0}
                  onClick={() => {
                    if (filteredCampaigns.length > MAX_CAMPAIGNS) {
                      toast.error(
                        <div>
                          <div>
                            You have selected more than 9 campaigns. To view the
                            report, please select up to 9 campaigns at a time.
                          </div>
                          <div style={{ marginTop: '12px' }}>
                            If you wish to see the data for all campaigns
                            together, leave the selection empty and download the
                            Excel file instead.
                          </div>
                        </div>,
                        {
                          autoClose: 8000,
                          closeButton: true,
                        }
                      )
                      return
                    }
                    const campaignsToSelect = filteredCampaigns.slice(
                      0,
                      MAX_CAMPAIGNS
                    )
                    const allIds = campaignsToSelect
                      .map((c) => c._id)
                      .filter((id): id is string => !!id)
                    setSelectedCampaigns(allIds)
                  }}
                  type="button"
                >
                  Select All
                </button>
                <button
                  className={styles.selectButton}
                  disabled={selectedCampaigns.length === 0}
                  onClick={() => {
                    setSelectedCampaigns([])
                    setBenchmarkedCampaign(null)
                  }}
                  type="button"
                >
                  Unselect All
                </button>
              </div>

              <div className={styles.campaignList}>
                {isLoading ? (
                  <div className={styles.loading}>Loading campaigns...</div>
                ) : filteredCampaigns.length === 0 ? (
                  <div className={styles.empty}>No campaigns found</div>
                ) : (
                  filteredCampaigns.map((campaign) => (
                    <div
                      className={`${styles.campaignItem} ${
                        selectedCampaigns.includes(campaign._id || '')
                          ? styles.selected
                          : ''
                      }`}
                      key={campaign._id}
                    >
                      <label className={styles.campaignLabel}>
                        <input
                          checked={selectedCampaigns.includes(
                            campaign._id || ''
                          )}
                          className={styles.checkbox}
                          disabled={
                            !selectedCampaigns.includes(campaign._id || '') &&
                            selectedCampaigns.length >= MAX_CAMPAIGNS
                          }
                          onChange={() =>
                            handleCampaignToggle(campaign._id || '')
                          }
                          type="checkbox"
                        />
                        <div className={styles.campaignInfo}>
                          <span className={styles.campaignName}>
                            {campaign.name}
                          </span>
                          {campaign.categories &&
                            campaign.categories.length > 0 && (
                              <span className={styles.campaignCategory}>
                                • {campaign.categories[0]}
                              </span>
                            )}
                        </div>
                      </label>
                      <button
                        className={`${styles.benchmarkButton} ${
                          benchmarkedCampaign === campaign._id
                            ? styles.active
                            : ''
                        }`}
                        onClick={() =>
                          handleBenchmarkToggle(campaign._id || '')
                        }
                        type="button"
                      >
                        <StarIcon />
                        Benchmark
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.exportButton}
            disabled={isExporting || selectedCampaigns.length === 1}
            onClick={handleExportExcel}
          >
            {isExporting ? (
              <div className={styles.exportLoader} />
            ) : (
              <ExportIcon />
            )}
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </button>
          <div className={styles.actionButtons}>
            <Button background="transparent" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              background="malibu"
              isDisabled={selectedCampaigns.length === 1}
              onClick={handleCompare}
            >
              Compare Campaigns
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
